/**
 * Was mehrere Abschnitte der Koordinationsseite gemeinsam brauchen — reine
 * Konstanten und reine Funktionen, kein Zustand. Das ist keine Ausnahme von
 * "kein geteilter Zustand über Modulgrenzen": Diese Werte ändern sich nie zur
 * Laufzeit, sie sind Übersetzungstabellen und Formatierer.
 *
 * --- 19.09.2026: vier Hexwerte, vier Merkmale -----------------------------
 *
 * Die Tabellen trugen `#2f7a3d` / `#8a6d1f` / `#b91c1c` / `#6b6b6b`. Das war
 * an den meisten Stellen HARMLOS, weil die Werte hier SCHLÜSSEL sind:
 * `farbzustand()` übersetzt sie in einen der fünf Zustände, und `StatusBadge`
 * holt die Farbe dann aus `--zustand-*`. Gerendert wurde der Hexwert nur an
 * zwei Stellen - `Baum.tsx` (Statusbalken) und `Detailkarte.tsx` (linke
 * Kante).
 *
 * Genau diese zwei waren nach dem Markenwechsel falsch: die Marke ist rot
 * geworden, `--zustand-fehler` ist auf Burgundy #6F0B10 ausgewichen - und der
 * Balken stand weiter auf dem alten #b91c1c. Eine Marke und ein Balken
 * daneben lagen bei ΔE00 = 3,24, also praktisch gleich, und der Balken
 * widersprach zugleich der Marke, die `farbzustand()` daraus ableitete.
 *
 * Jetzt stehen hier die Merkmale selbst. Schlüssel und Darstellung sind
 * damit wieder dasselbe, und `Baum.tsx`/`Detailkarte.tsx` zeigen dieselbe
 * Farbe wie die Marke daneben.
 *
 * WARUM NICHT `--konflikt` für die roten Einträge: Das wäre fachlich
 * naheliegend ("echte Kollision" ist ein Konflikt, kein Systemfehler), würde
 * aber `StatusBadge` einen SECHSTEN Zustand abverlangen - und den schließt
 * `StatusBadge.tsx` ausdrücklich aus ("ein sechster wird nicht erfunden").
 * Die Badges rendern seit jeher `fehler`; hier wird nichts umgedeutet,
 * sondern nur die direkte Darstellung an die Marke angeglichen, die ohnehin
 * schon gezeigt wurde. Ob die Koordination einen eigenen Zustand bekommt,
 * ist eine Entscheidung über die Musterbibliothek, nicht über diese Datei.
 */

export const GEWERK: Record<string, string> = {
  rlt: 'Lüftung', hzg: 'Heizung', klt: 'Kälte', san: 'Sanitär',
  spr: 'Sprinkler', elt: 'Elektro', arc: 'Architektur', twp: 'Tragwerk',
};

export const EINSTUFUNG: Record<string, { farbe: string; text: string }> = {
  echt: { farbe: 'var(--zustand-fehler)', text: 'echt' },
  pruefen: { farbe: 'var(--zustand-warnung)', text: 'prüfen' },
  vorgesehen: { farbe: 'var(--zustand-gut)', text: 'vorgesehen' },
  gewollt: { farbe: 'var(--zustand-gut)', text: 'gewollt' },
  selbstberuehrung: { farbe: 'var(--zustand-neutral)', text: 'Selbstberührung' },
  anschluss: { farbe: 'var(--zustand-neutral)', text: 'Anschluss' },
  unbeurteilbar: { farbe: 'var(--zustand-warnung)', text: 'unbeurteilbar' },

  // Nicht grün wie „gewollt": Ein Bauteil, das keines ist, ist nicht in
  // Ordnung — es ist gar nicht gefragt. Grau sagt das, grün sagte „geprüft
  // und richtig".
  kein_bauteil: { farbe: 'var(--zustand-neutral)', text: 'kein Bauteil' },
};

/**
 * Die Körbe des Arbeitsvorrats.
 *
 * --- Warum die Farbe nicht der Schwere folgt -------------------------------
 *
 * Sie folgt der HANDLUNG. 'sofort' ist grün, obwohl es echte
 * Durchdringungen sind — grün heisst hier „hier kommst du voran", nicht
 * „alles in Ordnung".
 *
 * Rot ist nur die Wiedervorlage: Dort wurde eine Zusage überschritten, und
 * das ist das Einzige in dieser Liste, was jemandem gegenüber unfair ist.
 *
 * Grau ist, was nicht Koordination ist ('eigenes_gewerk') oder was auf einen
 * fehlenden Rechenschritt wartet.
 */
export const KORB: Record<string, { farbe: string; text: string }> = {
  sofort: { farbe: 'var(--zustand-gut)', text: 'sofort umsetzbar' },
  wiedervorlage: { farbe: 'var(--zustand-fehler)', text: 'Wiedervorlage' },
  pumpe_pruefen: { farbe: 'var(--zustand-warnung)', text: 'Erzeuger prüfen' },
  gleicher_rang: { farbe: 'var(--zustand-warnung)', text: 'Regelfrage' },
  ohne_loesung: { farbe: 'var(--zustand-warnung)', text: 'Planungsentscheidung' },

  // Grün wie 'sofort', denn genau das ist es: Eine Kabeltrasse hat keinen
  // Druckverlust — hier fehlt kein Nachweis, es kann keinen geben. Grau wäre
  // die Farbe für 'da wartet noch etwas', und das wartet hier nicht.
  ohne_hydraulik: { farbe: 'var(--zustand-gut)', text: 'ohne Hydraulik' },

  nachweis_fehlt: { farbe: 'var(--zustand-neutral)', text: 'Nachweis fehlt' },
  ohne_vorschlag: { farbe: 'var(--zustand-neutral)', text: 'kein Vorschlag' },
  eigenes_gewerk: { farbe: 'var(--zustand-neutral)', text: 'eigenes Gewerk' },
};

/**
 * Der Status einer Anweisung (Batch 7) — dieselben vier Farben wie in
 * Detailkarte.tsx, hier einmal festgelegt statt an jeder Stelle neu
 * ausgerechnet.
 */
export const STATUS: Record<string, { farbe: string; text: string }> = {
  vorgeschlagen: { farbe: 'var(--zustand-neutral)', text: 'vorgeschlagen' },
  angenommen: { farbe: 'var(--zustand-gut)', text: 'angenommen' },
  umgesetzt: { farbe: 'var(--zustand-gut)', text: 'umgesetzt' },
  planer: { farbe: 'var(--zustand-warnung)', text: 'Planer gefragt' },
  abgelehnt: { farbe: 'var(--zustand-fehler)', text: 'abgelehnt' },
  ohne_anweisung: { farbe: 'var(--zustand-neutral)', text: 'ohne Anweisung' },
};

/**
 * Die Farbe je Lösungsart (Batch 7) — nach ZUSTÄNDIGKEIT eingefärbt (nicht je
 * einzelnem Schlüssel, davon kann es beliebig viele geben), aus denselben
 * vier Werten wie EINSTUFUNG/KORB/STATUS. 'weicher' ist die häufigste und
 * unauffälligste Lösung (grau), 'tragwerk'/'architektur' sind die
 * folgenreichsten (rot: ein anderes Gewerk ist betroffen), 'bleiber' und
 * 'beide' liegen dazwischen (Bernstein).
 */
export const LOESUNGSART_ZUSTAENDIG: Record<string, { farbe: string; text: string }> = {
  weicher: { farbe: 'var(--zustand-neutral)', text: 'weicher weicht' },
  bleiber: { farbe: 'var(--zustand-warnung)', text: 'bleiber betroffen' },
  beide: { farbe: 'var(--zustand-warnung)', text: 'beide betroffen' },
  tragwerk: { farbe: 'var(--zustand-fehler)', text: 'Tragwerk' },
  architektur: { farbe: 'var(--zustand-fehler)', text: 'Architektur' },
};

/** Eine Zahl deutsch formatiert — leer heisst 0, nicht "undefined". */
export const z = (x: number) => (x ?? 0).toLocaleString('de-DE');

/**
 * Eine Seite einer Regel in einer Zeile — leer heisst „trifft alles".
 *
 * Die Zeile ist bewusst technisch: Sie steht neben der Begründung im Klartext,
 * und wer prüfen will, OB die Regel das Richtige trifft, braucht die Kennungen
 * und nicht ihre Übersetzung. „Wand" statt „OST_Walls" wäre freundlicher und
 * unbrauchbar — im Modell steht OST_Walls.
 */
export function kriterium(
  seite: string,
  gewerk?: string | null,
  bauteilart?: string | null,
  kategorie?: string | null,
  typMuster?: string | null,
): string | null {
  const teile = [
    gewerk && `Gewerk ${gewerk}`,
    bauteilart && bauteilart,
    kategorie && kategorie,
    typMuster && `Name enthält: ${typMuster.split(',').join(' oder ')}`,
  ].filter(Boolean);

  return teile.length === 0 ? null : `${seite}: ${teile.join(', ')}`;
}

/**
 * Die vier Farben oben in einen Zustand der Musterbibliothek übersetzt
 * (Abschnitt 18 — Status nie nur über Farbe).
 *
 * Die Tabellen EINSTUFUNG/KORB/STATUS/LOESUNGSART_ZUSTAENDIG tragen bewusst
 * Farbwerte und keine Zustandsnamen: Sie beschreiben eine HANDLUNG, nicht eine
 * Bewertung ('sofort umsetzbar' ist grün, obwohl es eine echte Durchdringung
 * ist). Diese Funktion ist die einzige Stelle, an der die Farbe wieder zu
 * einem Zustand wird — sie steht hier, damit nicht jede Tabelle ihre eigene
 * Übersetzung mitbringt.
 */
export type Farbzustand = 'gut' | 'warnung' | 'fehler' | 'info' | 'neutral';

export function farbzustand(farbe: string | null | undefined): Farbzustand {
  switch (farbe) {
    case 'var(--zustand-gut)': return 'gut';
    case 'var(--zustand-warnung)': return 'warnung';
    case 'var(--zustand-fehler)': return 'fehler';
    default: return 'neutral';
  }
}

/**
 * Die vier Werte, die oben in den Tabellen stehen dürfen. Wer einen fünften
 * einträgt, bekommt von `farbzustand()` stillschweigend `neutral` - deshalb
 * stehen sie hier einmal benannt, und deshalb sind es Merkmale und keine
 * Hexwerte: ein Hexwert lässt sich vertippen, ein Merkmal nicht unbemerkt.
 */
export const ZUSTANDSFARBE = {
  gut: 'var(--zustand-gut)',
  warnung: 'var(--zustand-warnung)',
  fehler: 'var(--zustand-fehler)',
  neutral: 'var(--zustand-neutral)',
} as const;
